import React, { useState, useEffect } from 'react'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { Input, Select, Textarea } from './ui/Input'
import { type Requirement } from '../lib/api'

interface RequirementModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (requirement: Partial<Requirement>) => void
  requirement?: Requirement | null
  mode: 'add' | 'edit'
}

export const RequirementModal: React.FC<RequirementModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  requirement,
  mode
}) => {
  const [formData, setFormData] = useState<Partial<Requirement>>({
    requirement_id: '',
    title: '',
    description: '',
    category: [],
    priority: 'medium',
    is_mandatory: false,
    extraction_confidence: 'medium',
    page_number: null,
    weight: 1.0,
  })

  const [categoryInput, setCategoryInput] = useState('')

  useEffect(() => {
    if (mode === 'edit' && requirement) {
      setFormData({
        ...requirement,
        category: requirement.category || []
      })
    } else if (mode === 'add') {
      setFormData({
        requirement_id: `REQ-${Date.now()}`,
        title: '',
        description: '',
        category: [],
        priority: 'medium',
        is_mandatory: false,
        extraction_confidence: 'medium',
        page_number: null,
        weight: 1.0,
      })
    }
  }, [requirement, mode, isOpen])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit(formData)
    onClose()
  }

  const addCategory = () => {
    if (categoryInput.trim() && formData.category && !formData.category.includes(categoryInput.trim())) {
      setFormData({
        ...formData,
        category: [...formData.category, categoryInput.trim()]
      })
      setCategoryInput('')
    }
  }

  const removeCategory = (category: string) => {
    setFormData({
      ...formData,
      category: formData.category?.filter(c => c !== category) || []
    })
  }

  const priorityOptions = [
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ]

  const confidenceOptions = [
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ]

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'add' ? 'Add Requirement' : 'Edit Requirement'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Title"
          value={formData.title || ''}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Short requirement title"
          required
        />

        <div>
          <Textarea
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            rows={2}
            placeholder="Enter requirement description..."
          />
          <p className={`text-xs mt-1 ${(formData.description?.trim().length || 0) < 3 ? 'text-red-500' : 'text-gray-400'}`}>
            {formData.description?.trim().length || 0}/3 min characters
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Select
            label="Priority"
            value={formData.priority}
            onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
            options={priorityOptions}
            required
          />
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Categories
            </label>
            <div className="flex gap-1">
              <Input
                value={categoryInput}
                onChange={(e) => setCategoryInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addCategory()
                  }
                }}
                placeholder="Add..."
                className="text-sm"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addCategory}
                disabled={!categoryInput.trim()}
              >
                +
              </Button>
            </div>
          </div>
        </div>
        
        {formData.category && formData.category.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {formData.category.map((cat) => (
              <span
                key={cat}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
              >
                {cat}
                <button
                  type="button"
                  onClick={() => removeCategory(cat)}
                  className="ml-1 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_mandatory"
              checked={formData.is_mandatory}
              onChange={(e) => setFormData({ ...formData, is_mandatory: e.target.checked })}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="is_mandatory" className="text-sm font-medium text-gray-700">
              Mandatory
            </label>
          </div>

          <Input
            label="Page Number"
            type="number"
            value={formData.page_number || ''}
            onChange={(e) => setFormData({
              ...formData,
              page_number: e.target.value ? parseInt(e.target.value) : null
            })}
            min="1"
            max="10000"
            placeholder="Optional"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={!formData.title?.trim() || (formData.description?.trim().length || 0) < 3}
          >
            {mode === 'add' ? 'Add Requirement' : 'Update Requirement'}
          </Button>
        </div>
      </form>
    </Modal>
  )
}